import { Link } from 'react-router-dom';
import { Copy, Download, Star, Bot } from 'lucide-react';
import type { AdminLetterOutDetail, LetterRating, LetterAutoReview } from '../../../lib/api';

interface LetterHeaderProps {
  letter: AdminLetterOutDetail;
  rating: LetterRating | null;
  autoReview?: LetterAutoReview | null;
  onCopyLetter: () => void;
  onCopyDump: () => void;
  onDownloadDump: () => void;
}

function getStatusColor(status: string) {
  switch (status) {
    case 'sent':
      return 'bg-success/20 text-success';
    case 'failed':
      return 'bg-ember/20 text-ember';
    case 'queued':
      return 'bg-amber-500/20 text-amber-400';
    case 'generating':
      return 'bg-rune/20 text-rune';
    default:
      return 'bg-stone/30 text-mist';
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

export function LetterHeader({
  letter,
  rating,
  autoReview,
  onCopyLetter,
  onCopyDump,
  onDownloadDump,
}: LetterHeaderProps) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl p-6 space-y-4">
      {/* QA Badge */}
      {letter.isQa && (
        <div className="rounded-lg border border-rune/40 bg-rune/10 px-3 py-2 text-xs text-rune inline-block">
          QA Mail
        </div>
      )}

      {/* Title Row */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl text-parchment mb-2">{letter.subject}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-mist">
            <span className="px-2 py-0.5 bg-stone/30 rounded text-xs uppercase tracking-wide">
              {letter.type}
            </span>
            <span>To: {letter.user.email}</span>
            <span>World: {letter.worldAddress}</span>
          </div>
        </div>

        {/* Status and Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-3 py-1 rounded text-sm ${getStatusColor(letter.status)}`}>
            {letter.status}
          </span>
          {rating && (
            <span className="flex items-center gap-1 px-2 py-1 bg-rune/20 text-rune rounded text-sm">
              <Star size={14} className="fill-rune" />
              {rating.rating}/5
            </span>
          )}
          {autoReview && (
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-300 rounded text-sm">
              <Bot size={14} />
              {autoReview.rating}/10
            </span>
          )}
          <button
            onClick={onCopyLetter}
            className="flex items-center gap-1 px-3 py-1 rounded text-xs border border-stone/30 text-mist hover:text-parchment hover:border-stone/50 transition-colors"
          >
            <Copy size={12} />
            Copy Letter
          </button>
          <button
            onClick={onCopyDump}
            className="flex items-center gap-1 px-3 py-1 rounded text-xs border border-stone/30 text-mist hover:text-parchment hover:border-stone/50 transition-colors"
          >
            <Copy size={12} />
            Copy Dump
          </button>
          <button
            onClick={onDownloadDump}
            className="flex items-center gap-1 px-3 py-1 rounded text-xs border border-stone/30 text-mist hover:text-parchment hover:border-stone/50 transition-colors"
          >
            <Download size={12} />
            Download
          </button>
        </div>
      </div>

      {/* Date Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-mist mb-1">Scheduled For</div>
          <div className="text-parchment">{formatDate(letter.scheduledFor)}</div>
        </div>
        <div>
          <div className="text-mist mb-1">Created</div>
          <div className="text-parchment">{formatDate(letter.createdAt)}</div>
        </div>
        <div>
          <div className="text-mist mb-1">Sent</div>
          <div className="text-parchment">{letter.sentAt ? formatDate(letter.sentAt) : '-'}</div>
        </div>
        <div>
          <div className="text-mist mb-1">Retries</div>
          <div className="text-parchment">{letter.retryCount}</div>
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-wrap gap-4 pt-2 border-t border-stone/20 text-sm">
        <div>
          <span className="text-mist">User: </span>
          <Link to={`/admin/users/${letter.user.id}`} className="text-rune hover:underline">
            {letter.user.email}
          </Link>
        </div>
        <div>
          <span className="text-mist">World: </span>
          <Link to={`/admin/worlds/${letter.worldId}`} className="text-rune hover:underline">
            {letter.worldAddress}
          </Link>
        </div>
        <div>
          <span className="text-mist">Letter ID: </span>
          <span className="font-mono text-xs text-parchment">{letter.id}</span>
        </div>
      </div>
    </div>
  );
}
